import { useEffect, useState } from 'react';
import { SlimBanner, type SlimBannerProps } from '@stitch-fix/mode-react';
import { IconError16 } from '@stitch-fix/mode-react/icons';
import { useSelectEvent, useViewEvent, ViewEvent, type SelectEvent } from '@stitch-fix/event-reporter';
import type { ModeBannerSlimVariant, ModeIcon } from '../../../types/definitions/vendor/graphql-schema.d';
import type { GetLookbookGalleryQuery } from './graphql/getLookbookGallery';
const SLIM_VARIANT_MAP: Record<ModeBannerSlimVariant, SlimBannerProps['variant']> = {
  DEFAULT: 'info-light',
  INVERSE: 'dark',
  PROMOTIONAL: 'promo-dark',
  URGENT: 'error-dark',
  CITRUS: 'citrus',
  GRAPEFRUIT: 'grapefruit',
  FOREST: 'forest',
  NAVY_WHITE: 'navy-white',
  NAVY_CITRUS: 'navy-citrus',
  CERULEAN_WHITE: 'cerulean-white',
  KHAKI: 'khaki',
  CARGO: 'cargo',
  CHARCOAL: 'charcoal',
  BLUE_95: 'blue-95',
  MINT_95: 'mint-95',
  GRAY_99: 'gray-99',
  SHORELINE: 'shoreline',
  BUTTER: 'butter',
  DENIM: 'denim',
  BLUSH: 'blush'
} as const;
const SlimBannerIcon = ({
  icon
}: {
  icon?: ModeIcon | null;
}) => {
  switch (icon) {
    case 'ERROR':
      return <IconError16 title="error" />;
    default:
      return null;
  }
};
const buildBannerCtaUrl = (url: string | null | undefined) => {
  if (!url) {
    return undefined;
  }
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.pathname === '/profile/photos') {
      parsedUrl.searchParams.set('visionNotification', 'true');
    }
    return parsedUrl.toString();
  } catch (_error) {
    return url ?? undefined;
  }
};
type VisionNotificationBannerProps = {
  notification: NonNullable<NonNullable<GetLookbookGalleryQuery['client']>['vision']['notification']>;
  onDismiss: () => void;
};
const STORAGE_KEY_PREFIX = 'sf-vision-notification-dismissed:';
const VisionNotificationBanner = ({
  notification,
  onDismiss
}: VisionNotificationBannerProps) => {
  const {
    banner,
    productTrackingName,
    productTrackingContentId,
    productTrackingActionName
  } = notification;
  const storageKey = `${STORAGE_KEY_PREFIX}${banner.id}`;
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(storageKey) === 'true';
  });
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    setIsDismissed(sessionStorage.getItem(storageKey) === 'true');
  }, [storageKey]);
  const {
    ref,
    EventProvider
  } = useViewEvent({
    schema: 'view',
    name: productTrackingName as ViewEvent['name'],
    content_id: productTrackingContentId
  });
  const {
    reportSelectEvent
  } = useSelectEvent();
  if (isDismissed) {
    return null;
  }
  const bannerCtaUrl = buildBannerCtaUrl(banner?.cta?.url);
  const persistDismissal = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(storageKey, 'true');
    }
    setIsDismissed(true);
    onDismiss();
  };
  const handleDismiss = () => {
    persistDismissal();
  };
  const handleClose = banner.cta ? undefined : handleDismiss;
  return <EventProvider>
      <div ref={ref}>
        <SlimBanner data-testid="vision-notification-banner" icon={banner.icon ? <SlimBannerIcon icon={banner.icon} /> : undefined} text={banner.message} variant={SLIM_VARIANT_MAP[banner.variant] ?? 'info-light'} cta={banner.cta && bannerCtaUrl ? {
        text: banner.cta.label.text,
        url: bannerCtaUrl,
        onClick: () => {
          persistDismissal();
          reportSelectEvent({
            schema: 'select',
            name: productTrackingName as SelectEvent['name'],
            action_name: productTrackingActionName as SelectEvent['action_name']
          });
        }
      } : undefined} onClose={handleClose} />
      </div>
    </EventProvider>;
};
export default VisionNotificationBanner;